import { useMemo } from 'react';

import type { SortableCollectionListBaseProps } from '@/components/blocks/editor/collection/collection-dnd';
import {
  Accordion,
  type AccordionButtonVariant,
  type AccordionIconStyle,
} from '@/components/ui/accordion';
import { useAccordionValue } from '@/hooks/use-accordion-value';
import { useEffectiveColors } from '@/hooks/use-public-design-settings';
import type { CollectionItem, CollectionLayoutConfig } from '@/lib/blocks/collection/blocks';
import {
  getAccordionSingleGapClassName,
  getCardBgClassName,
  getCollectionContainerClassName,
} from '@/lib/blocks/collection/blocks';
import { useCollectionItemIdsWithChanges } from '@/lib/section-proposal-diff/section-review-context';
import { cn } from '@/lib/utils';
import { SortableAccordionCard } from './accordion-card';

export interface AccordionOptions {
  showDividers: boolean;
  iconStyle: AccordionIconStyle;
  buttonVariant: AccordionButtonVariant;
  expandFirstItem: boolean;
  allowMultiple: boolean;
}

interface SortableAccordionListProps extends SortableCollectionListBaseProps {
  accordionOptions: AccordionOptions;
  accordionLayout: CollectionLayoutConfig | null;
  accordionContainerPaddingClass: string;
}

export function SortableAccordionList({
  items,
  cardConfig,
  cardStyle,
  accordionOptions,
  accordionLayout,
  accordionContainerPaddingClass,
  collectionSortableGroup,
  collectionItemDragData,
  disabled,
  actionsDisabled,
  selectedItemId,
  onUpdate,
  onDelete,
  onDuplicate,
  onSelectionChange,
}: SortableAccordionListProps) {
  const cardColors = useEffectiveColors();
  const { expandFirstItem, allowMultiple, showDividers, iconStyle, buttonVariant } =
    accordionOptions;
  const changedItemIds = useCollectionItemIdsWithChanges();
  // changedItemIds is a new Set every render, so turn it into a string key first and only
  // build a new array when that key actually changes. Without this, openItemIds would be a
  // new array every render, which retriggers an effect in useAccordionValue and loops forever.
  const changedItemIdsKey = [...changedItemIds].sort().join(',');
  const openItemIds = useMemo(
    () => changedItemIdsKey.split(',').filter(Boolean),
    [changedItemIdsKey],
  );
  const [accordionValue, setAccordionValue] = useAccordionValue({
    expandFirstItem,
    firstItemId: items[0]?.id,
    openItemIds,
  });

  const sortIndexById = new Map(items.map((item, index) => [item.id, index]));

  const sharedCardProps = (item: CollectionItem) => ({
    item,
    totalCount: items.length,
    sortIndex: sortIndexById.get(item.id) ?? -1,
    collectionSortableGroup,
    collectionItemDragData,
    cardConfig,
    showDividers: false,
    iconStyle,
    buttonVariant,
    disabled,
    actionsDisabled,
    primarySelected: selectedItemId === item.id,
    onUpdate,
    onDelete: () => onDelete(item.id),
    onDuplicate: () => onDuplicate(item.id),
    onSelectionChange: (selected: boolean) => onSelectionChange(item.id, selected),
  });

  const cardBgClassName = getCardBgClassName(cardStyle, cardColors);

  const singleColumnClassName = cn(
    'w-full',
    getAccordionSingleGapClassName(accordionLayout?.gap ?? 'sm'),
    showDividers && 'divide-y divide-border',
    accordionContainerPaddingClass,
    cardConfig.align === 'center' && 'text-center',
    cardConfig.align === 'end' && 'text-right',
    cardBgClassName,
  );

  const singleColumnCards = items.map((item) => (
    <SortableAccordionCard key={item.id} {...sharedCardProps(item)} showDividers={showDividers} />
  ));

  if (accordionLayout && accordionLayout.columns.desktop > 1) {
    return (
      <div className={cn(getCollectionContainerClassName(accordionLayout), 'items-start')}>
        {items.map((item) => (
          <div
            key={item.id}
            className={cn(
              'rounded-md border px-4',
              cardConfig.align === 'center' && 'text-center',
              cardConfig.align === 'end' && 'text-right',
              cardBgClassName,
            )}
            style={cardStyle}
          >
            {allowMultiple ? (
              <Accordion
                type="multiple"
                value={accordionValue.includes(item.id) ? [item.id] : []}
                onValueChange={(vals) => {
                  setAccordionValue((prev) => [...prev.filter((id) => id !== item.id), ...vals]);
                }}
              >
                <SortableAccordionCard {...sharedCardProps(item)} />
              </Accordion>
            ) : (
              <Accordion
                type="single"
                collapsible
                value={accordionValue.includes(item.id) ? item.id : ''}
                onValueChange={(v) => setAccordionValue(v ? [v] : [])}
              >
                <SortableAccordionCard {...sharedCardProps(item)} />
              </Accordion>
            )}
          </div>
        ))}
      </div>
    );
  }

  const multipleOpen = allowMultiple || changedItemIds.size > 1;

  if (multipleOpen) {
    return (
      <Accordion
        type="multiple"
        value={accordionValue}
        onValueChange={setAccordionValue}
        className={singleColumnClassName}
        style={cardStyle}
      >
        {singleColumnCards}
      </Accordion>
    );
  }

  // A changed item must win the single-open slot even if another item (e.g. via
  // expandFirstItem) is also present in accordionValue.
  const singleOpenValue =
    changedItemIds.size > 0 ? [...changedItemIds][0] : (accordionValue[0] ?? '');

  return (
    <Accordion
      type="single"
      collapsible
      value={singleOpenValue}
      onValueChange={(v) => setAccordionValue(v ? [v] : [])}
      className={singleColumnClassName}
      style={cardStyle}
    >
      {singleColumnCards}
    </Accordion>
  );
}
